import React from "react";
import { Mail, MapPin, Linkedin, ArrowRight } from "./Icons";

const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/images/logo-d-192.png`;

const COMPANY_LINKS = [
  { id: "home",      label: "Home" },
  { id: "about",     label: "Our Studio" },
  { id: "ecosystem", label: "Our Products" },
  { id: "careers",   label: "Join the Core" },
  { id: "faq",       label: "Engine Room" },
  { id: "contact",   label: "Launch Project" },
];

const PRODUCT_LINKS = [
  { id: "ecosystem", label: "nBubble" },
  { id: "ecosystem", label: "App Search" },
];

const CLIENT_LINKS = [
  { href: "https://aditirealty.com", label: "AditiRealty.com" },
  { href: "https://www.airxsolar.com.au/", label: "AirXSolar.com" },
];

const SOCIALS = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/dowblelabs/", label: "LinkedIn" },
];

function FooterLinkButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="group inline-flex items-center gap-1.5 text-zinc-600 hover:text-zinc-900 text-sm transition-colors text-left"
    >
      <span className="w-0 group-hover:w-3 h-px bg-accent-500 transition-all duration-300" />
      {children}
    </button>
  );
}

export default function Footer({ navigate }) {
  return (
    <footer className="relative border-t border-zinc-200 bg-white/60">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-14">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <img
                src={LOGO_SRC}
                alt="Dowble"
                width="36"
                height="36"
                className="w-9 h-9 rounded-xl object-cover shadow-glow ring-1 ring-zinc-900/5"
              />
              <span className="text-zinc-900 font-bold text-lg tracking-tight">Dowble</span>
            </div>
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              We build, deploy, scale, and maintain high-performance digital ecosystems.
              Concept to production in&nbsp;15&nbsp;days — from Bengaluru, used worldwide.
            </p>
            <div className="flex items-center gap-3">
              {SOCIALS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 hover:border-zinc-300 flex items-center justify-center text-zinc-500 hover:text-zinc-900 transition-all shadow-sm"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <p className="text-zinc-500 text-xs font-semibold uppercase tracking-[0.16em] mb-4">Company</p>
            <ul className="space-y-2.5">
              {COMPANY_LINKS.map((link) => (
                <li key={link.id + link.label}>
                  <FooterLinkButton onClick={() => navigate(link.id)}>{link.label}</FooterLinkButton>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <p className="text-zinc-500 text-xs font-semibold uppercase tracking-[0.16em] mb-4">Our Apps</p>
            <ul className="space-y-2.5 mb-6">
              {PRODUCT_LINKS.map((link) => (
                <li key={link.label}>
                  <FooterLinkButton onClick={() => navigate(link.id)}>{link.label}</FooterLinkButton>
                </li>
              ))}
            </ul>
            <p className="text-zinc-500 text-xs font-semibold uppercase tracking-[0.16em] mb-4">Client Work</p>
            <ul className="space-y-2.5">
              {CLIENT_LINKS.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-600 hover:text-zinc-900 text-sm transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-zinc-500 text-xs font-semibold uppercase tracking-[0.16em] mb-4">Contact</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <Mail size={15} className="text-accent-600 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-600 text-sm">contact@dowble.com</span>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin size={15} className="text-accent-600 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-600 text-sm">Sarjapur, Bengaluru, Karnataka, India</span>
              </li>
            </ul>
            <div className="mt-6 p-5 rounded-2xl border border-zinc-200 bg-gradient-to-br from-accent-500/[0.08] to-violet-500/[0.05]">
              <p className="text-zinc-600 text-xs mb-1.5">Ready to build?</p>
              <button
                onClick={() => navigate("contact")}
                className="group inline-flex items-center gap-1.5 text-zinc-900 text-sm font-semibold"
              >
                Start your project
                <ArrowRight size={14} className="text-accent-600 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-zinc-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">
            © {new Date().getFullYear()} Dowble Technologies Pvt. Ltd. All rights reserved.
          </p>
          <ul className="flex items-center gap-6">
            {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((item) => (
              <li key={item}>
                <button className="text-zinc-500 hover:text-zinc-800 text-xs transition-colors">
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
